import type { ApiClient } from '../../../shared/api/api-client';
import type { SuperadminPlatformDashboardDto } from './platform-dashboard.types';

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function parseAlert(raw: unknown): SuperadminPlatformDashboardDto['alerts'][number] | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const type = o.type;
  if (type !== 'warning' && type !== 'danger' && type !== 'info') return null;
  const title = typeof o.title === 'string' ? o.title : '';
  const description = typeof o.description === 'string' ? o.description : '';
  const ar = o.action_route;
  const action_route = ar === null || ar === undefined ? null : typeof ar === 'string' ? ar : null;
  return { type, title, description, action_route };
}

export function parseSuperadminPlatformDashboard(raw: unknown): SuperadminPlatformDashboardDto | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const org = o.organizations;
  const pl = o.plans;
  const pu = o.platform_users;
  const al = o.alerts;
  if (!org || typeof org !== 'object' || Array.isArray(org)) return null;
  if (!pl || typeof pl !== 'object' || Array.isArray(pl)) return null;
  if (!pu || typeof pu !== 'object' || Array.isArray(pu)) return null;
  const orgO = org as Record<string, unknown>;
  const plO = pl as Record<string, unknown>;
  const puO = pu as Record<string, unknown>;
  const alertsRaw = Array.isArray(al) ? al : [];
  const alerts: SuperadminPlatformDashboardDto['alerts'] = [];
  for (const a of alertsRaw) {
    const p = parseAlert(a);
    if (p) alerts.push(p);
  }
  return {
    organizations: {
      total: num(orgO.total),
      active: num(orgO.active),
      inactive: num(orgO.inactive),
      removed: num(orgO.removed),
      without_plan: num(orgO.without_plan),
      near_expiration: num(orgO.near_expiration)
    },
    plans: {
      total: num(plO.total),
      active: num(plO.active)
    },
    platform_users: {
      total: num(puO.total),
      active: num(puO.active)
    },
    alerts
  };
}

export async function fetchSuperadminPlatformDashboard(apiClient: ApiClient, signal?: AbortSignal): Promise<SuperadminPlatformDashboardDto> {
  const raw = await apiClient.request<unknown>('/superadmin/dashboard', { method: 'GET', signal });
  const parsed = parseSuperadminPlatformDashboard(raw);
  if (!parsed) {
    throw new Error('invalid_dashboard_payload');
  }
  return parsed;
}
